import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ArtistGroupMember,
  ArtistGroupMemberAddRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import { artistQueryKey } from './useArtistQuery';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number, op: string): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Artist member ${op} failed with status ${status}`, raw);
};

export interface AddMemberInput {
  groupId: string;
  body: ArtistGroupMemberAddRequest;
}

export const useAddArtistMemberMutation = () => {
  const qc = useQueryClient();
  return useMutation<ArtistGroupMember, Error, AddMemberInput>({
    mutationFn: async ({ groupId, body }) => {
      const path = '/admin/v1/artist-groups/{groupId}/members' as const;
      const concretePath = path.replace('{groupId}', groupId);
      const idempotencyKey = idempotencyKeyFor('POST', concretePath, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: { path: { groupId }, header: { 'Idempotency-Key': idempotencyKey } },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status, 'add');
      return (data as { data: ArtistGroupMember }).data;
    },
    onSuccess: (_member, { groupId }) => {
      void qc.invalidateQueries({ queryKey: artistQueryKey(groupId) });
    },
  });
};

export interface RemoveMemberInput {
  groupId: string;
  memberLinkId: string;
  leftAt?: string;
}

export const useRemoveArtistMemberMutation = () => {
  const qc = useQueryClient();
  return useMutation<ArtistGroupMember, Error, RemoveMemberInput>({
    mutationFn: async ({ groupId, memberLinkId, leftAt }) => {
      const path = '/admin/v1/artist-groups/{groupId}/members/{memberLinkId}' as const;
      const concretePath = path
        .replace('{groupId}', groupId)
        .replace('{memberLinkId}', memberLinkId);
      const idempotencyKey = idempotencyKeyFor('DELETE', concretePath, leftAt ?? '');
      const { data, error, response } = await apiClient.raw.DELETE(path, {
        params: {
          path: { groupId, memberLinkId },
          header: { 'Idempotency-Key': idempotencyKey },
          ...(leftAt ? { query: { leftAt } } : {}),
        },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status, 'remove');
      return (data as { data: ArtistGroupMember }).data;
    },
    onSuccess: (_member, { groupId }) => {
      void qc.invalidateQueries({ queryKey: artistQueryKey(groupId) });
    },
  });
};
